import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

/**
 * Inline confirm dialog for "Close as resolved" (CEO-only, non-terminal task
 * → done). Requires a short note.
 */
export function CloseResolvedDialog({ open, onOpenChange, taskId, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      toast.error("A resolution note is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tasks/${taskId}/close`, { note: trimmed });
      toast.success("Task closed as resolved.");
      setNote("");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) setNote(""); } }}>
      <DialogContent data-testid="close-resolved-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Close as resolved</DialogTitle>
          <DialogDescription>
            Mark this task as done without going through the agent pipeline — for work handled elsewhere.
            A short note is required and will be saved on the task's timeline.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="close-resolved-note"
          placeholder="Resolved by… (e.g. “handled directly on the VPS”, “duplicate of T-abc already shipped”)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          autoFocus
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="close-resolved-cancel" disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !note.trim()}
            data-testid="close-resolved-confirm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Close as resolved
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
